import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Search,
  Users,
  Leaf,
  Recycle,
  Lightbulb,
  Shield,
  MapPin,
  Star,
  TrendingUp,
} from "lucide-react";
import Map from "@/components/Map";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import ListingCard from "@/components/ListingCard";
import ListingCardSkeleton from "@/components/ListingCardSkeleton";
import { useNewestListings } from "@/hooks/useNewestListings";
import { useAllBreeders } from "@/hooks/useAllBreeders";
import { useTopBreeders } from "@/hooks/useTopBreeders";
import BreederProfileCard from "@/components/BreederProfileCard";

const Index = () => {
  const { user, profile } = useAuth();
  const navigate = useNavigate();
  const { data: newestListings = [], isLoading, error } = useNewestListings();
  const {
    data: breeders = [],
    isLoading: breedersLoading,
    error: breedersError,
  } = useAllBreeders();
  const {
    data: topBreeders = [],
    isLoading: topBreedersLoading,
    error: topBreedersError,
  } = useTopBreeders();

  const [activeTab, setActiveTab] = useState("all");

  // Handle Join as Breeder button click
  const handleJoinAsBreeder = () => {
    if (user && profile) {
      // Navigate to appropriate dashboard based on user type
      if (profile.user_type === "admin") {
        navigate("/admin-dashboard");
      } else if (profile.user_type === "breeder") {
        navigate("/breeder-dashboard");
      } else {
        navigate("/buyer-dashboard");
      }
    } else {
      // Not signed in, go to signup
      navigate("/signup");
    }
  };

  // Filter listings based on selected tab
  const filterListingsByType = (type: string) => {
    if (type === "all") return newestListings;
    return newestListings.filter((listing) =>
      listing.type?.toLowerCase().includes(type.toLowerCase())
    );
  };

  // Get filtered listings for current tab
  const filteredListings = filterListingsByType(activeTab);

  const renderListingsContent = (listings: typeof newestListings) => {
    if (error) {
      return (
        <div className="text-center py-12 animate-fade-in">
          <p className="text-gray-600">
            Unable to load listings at the moment.
          </p>
        </div>
      );
    }

    if (!isLoading && listings.length === 0) {
      return (
        <div className="text-center py-12 animate-fade-in">
          <p className="text-gray-600">
            {activeTab === "all"
              ? "No listings available yet. Be the first to add one!"
              : `No ${activeTab} listings available yet.`}
          </p>
        </div>
      );
    }

    // Show skeleton loading for carousel structure
    if (isLoading) {
      return (
        <div className="relative">
          <Carousel className="w-full">
            <CarouselContent className="-ml-2 md:-ml-4">
              {Array.from({ length: 4 }).map((_, index) => (
                <CarouselItem
                  key={`skeleton-${index}`}
                  className="pl-2 md:pl-4 basis-full sm:basis-1/2 md:basis-1/3 lg:basis-1/4"
                >
                  <div
                    className="animate-fade-in"
                    style={{ animationDelay: `${index * 100}ms` }}
                  >
                    <ListingCardSkeleton />
                  </div>
                </CarouselItem>
              ))}
            </CarouselContent>
            <div className="flex justify-center items-center gap-4 mt-6">
              <CarouselPrevious className="static translate-y-0 translate-x-0" />
              <CarouselNext className="static translate-y-0 translate-x-0" />
            </div>
          </Carousel>
        </div>
      );
    }

    // Show actual listings with smooth entrance animation
    return (
      <div className="relative">
        <Carousel className="w-full">
          <CarouselContent className="-ml-2 md:-ml-4">
            {listings.map((listing, index) => (
              <CarouselItem
                key={`${listing.id}-${activeTab}`}
                className="pl-2 md:pl-4 basis-full sm:basis-1/2 md:basis-1/3 lg:basis-1/4"
              >
                <div
                  className="opacity-0 animate-fade-in"
                  style={{
                    animationDelay: `${index * 50}ms`,
                    animationFillMode: "forwards",
                  }}
                >
                  <ListingCard listing={listing} />
                </div>
              </CarouselItem>
            ))}
          </CarouselContent>

          <div className="flex justify-center items-center gap-4 mt-6">
            <CarouselPrevious className="static translate-y-0 translate-x-0" />
            <CarouselNext className="static translate-y-0 translate-x-0" />
          </div>
        </Carousel>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-green-50 to-white">
      <Navbar />

      {/* Hero Section with Farm Background */}
      <section
        className="relative py-44 px-4 sm:px-6 lg:px-8 bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage:
            "linear-gradient(rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.6)), url(/cows-in-mountains.jpg)",
        }}
      >
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="text-left">
              <h1
                className="text-4xl md:text-6xl font-bold text-white mb-6 opacity-0 animate-fade-in"
                style={{
                  animationDelay: "200ms",
                  animationFillMode: "forwards",
                }}
              >
                Connect with Elite
                <span
                  className="text-green-400 block opacity-0 animate-fade-in"
                  style={{
                    animationDelay: "400ms",
                    animationFillMode: "forwards",
                  }}
                >
                  Cattle Breeders
                </span>
              </h1>
              <p
                className="text-xl text-white mb-8 max-w-2xl opacity-0 animate-fade-in"
                style={{
                  animationDelay: "600ms",
                  animationFillMode: "forwards",
                }}
              >
                Join the Worldwide network of modern cattle producers — build
                your brand, your herd, your legacy. Welcome to the Cattle Club
              </p>
              <div
                className="flex flex-col sm:flex-row gap-4 opacity-0 animate-fade-in"
                style={{
                  animationDelay: "800ms",
                  animationFillMode: "forwards",
                }}
              >
                <Link to="/marketplace">
                  <Button
                    size="lg"
                    className="bg-green-700 hover:bg-green-800 text-lg px-8 py-3 hover:scale-105 transition-all duration-200"
                  >
                    <Search className="w-5 h-5 mr-2" />
                    Browse Cattle
                  </Button>
                </Link>
                <Button
                  size="lg"
                  variant="outline"
                  className="text-lg px-8 py-3 w-auto self-start bg-white/10 border-white text-white hover:bg-white hover:text-gray-900 hover:scale-105 transition-all duration-200"
                  onClick={handleJoinAsBreeder}
                >
                  <Users className="w-5 h-5 mr-2" />
                  {user ? "Go to Dashboard" : "Join as Breeder or Buyer"}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Marketplace Connection Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="order-2 lg:order-1">
              <div
                className="flex items-center mb-6 opacity-0 animate-fade-in"
                style={{
                  animationDelay: "100ms",
                  animationFillMode: "forwards",
                }}
              >
                <div className="w-12 h-1 bg-green-600 mr-4"></div>
                <span className="text-green-700 font-semibold text-lg">
                  Global Marketplace
                </span>
              </div>
              <h2
                className="text-4xl font-bold text-gray-900 mb-6 opacity-0 animate-fade-in"
                style={{
                  animationDelay: "200ms",
                  animationFillMode: "forwards",
                }}
              >
                Connecting Breeders & Buyers Worldwide
              </h2>
              <p
                className="text-gray-600 text-lg mb-8 leading-relaxed opacity-0 animate-fade-in"
                style={{
                  animationDelay: "300ms",
                  animationFillMode: "forwards",
                }}
              >
                urCattle is the premier global marketplace where cattle breeders
                showcase their finest livestock and buyers discover exceptional
                genetics. Our platform is a first of its kind, bridging the gap
                between passionate breeders and buyers. Sign up today and put
                your herd on the map.
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                <div
                  className="flex items-start space-x-4 opacity-0 animate-fade-in"
                  style={{
                    animationDelay: "400ms",
                    animationFillMode: "forwards",
                  }}
                >
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 hover:scale-110 transition-transform duration-200">
                    <MapPin className="w-6 h-6 text-green-700" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">
                      Global Reach
                    </h4>
                    <p className="text-sm text-gray-600">
                      Connect with breeders and buyers across continents
                    </p>
                  </div>
                </div>
                <div
                  className="flex items-start space-x-4 opacity-0 animate-fade-in"
                  style={{
                    animationDelay: "500ms",
                    animationFillMode: "forwards",
                  }}
                >
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 hover:scale-110 transition-transform duration-200">
                    <Star className="w-6 h-6 text-green-700" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">
                      Elite Quality
                    </h4>
                    <p className="text-sm text-gray-600">
                      Verified breeders with exceptional livestock
                    </p>
                  </div>
                </div>
                <div
                  className="flex items-start space-x-4 opacity-0 animate-fade-in"
                  style={{
                    animationDelay: "600ms",
                    animationFillMode: "forwards",
                  }}
                >
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 hover:scale-110 transition-transform duration-200">
                    <TrendingUp className="w-6 h-6 text-green-700" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">
                      Market Growth
                    </h4>
                    <p className="text-sm text-gray-600">
                      Expanding opportunities for all participants
                    </p>
                  </div>
                </div>
                <div
                  className="flex items-start space-x-4 opacity-0 animate-fade-in"
                  style={{
                    animationDelay: "700ms",
                    animationFillMode: "forwards",
                  }}
                >
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 hover:scale-110 transition-transform duration-200">
                    <Users className="w-6 h-6 text-green-700" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">
                      Trusted Network
                    </h4>
                    <p className="text-sm text-gray-600">
                      Verified community of cattle professionals
                    </p>
                  </div>
                </div>
              </div>

              <div
                className="text-center lg:text-left opacity-0 animate-fade-in"
                style={{
                  animationDelay: "800ms",
                  animationFillMode: "forwards",
                }}
              >
                <Link to="/marketplace">
                  <Button className="bg-green-700 hover:bg-green-800 mt-4 hover:scale-105 transition-all duration-200">
                    Explore the Marketplace
                  </Button>
                </Link>
              </div>
            </div>

            <div
              className="order-1 lg:order-2 relative flex justify-center opacity-0 animate-fade-in"
              style={{ animationDelay: "300ms", animationFillMode: "forwards" }}
            >
              <div className="relative group cursor-pointer">
                {/* Background shape - animated on hover */}
                <div
                  className="absolute inset-0 bg-gradient-to-br from-green-100 via-green-50 to-green-100 rounded-2xl shadow-lg transition-all duration-500 ease-in-out group-hover:rotate-3 group-hover:bg-gradient-to-br group-hover:from-green-200 group-hover:via-green-100 group-hover:to-green-200"
                  style={{ height: "60%", top: "20%" }}
                ></div>

                {/* Image container - animated on hover */}
                <div className="relative z-10 transition-all duration-500 ease-in-out group-hover:-rotate-2">
                  <img
                    src="/black-bull.png"
                    alt="Professional cattle breeder"
                    className="rounded-2xl shadow-xl w-full h-auto max-w-sm md:max-w-md lg:max-w-lg object-cover"
                  />
                </div>

                {/* 100% Verified badge */}
                <div className="absolute right-2 bottom-2 bg-green-700 text-white p-4 lg:p-8 rounded-xl shadow-2xl z-20 border-4 border-white max-w-xs hover:scale-110 transition-transform duration-200">
                  <div className="text-2xl md:text-3xl lg:text-4xl font-bold text-center">
                    100%
                  </div>
                  <div className="text-xs md:text-sm lg:text-base flex items-center justify-center mt-1">
                    <Shield className="w-3 h-3 md:w-4 md:h-4 lg:w-5 lg:h-5 mr-1" />
                    VERIFIED
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Newest Listings Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
        {/* Gradient background */}
        <div className="absolute inset-0 bg-gradient-to-br from-emerald-50 via-green-100 to-teal-50"></div>

        {/* Gradient overlay with better contrast */}
        <div className="absolute inset-0 bg-gradient-to-r from-green-600/15 via-emerald-500/10 to-teal-600/15"></div>

        {/* Gradient orbs for depth */}
        <div className="absolute -top-32 -left-32 w-96 h-96 bg-gradient-to-r from-green-400/30 to-emerald-400/20 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute top-1/4 right-1/3 w-72 h-72 bg-gradient-to-r from-teal-300/25 to-green-300/35 rounded-full blur-2xl"></div>
        <div className="absolute -bottom-24 -right-24 w-80 h-80 bg-gradient-to-r from-emerald-400/25 to-teal-400/30 rounded-full blur-3xl"></div>

        {/* Accent orb */}
        <div className="absolute top-3/4 left-1/5 w-48 h-48 bg-gradient-to-r from-green-200/40 to-emerald-200/30 rounded-full blur-2xl"></div>

        <div className="max-w-7xl mx-auto relative z-10">
          <div
            className="text-center mb-8 opacity-0 animate-fade-in"
            style={{ animationDelay: "100ms", animationFillMode: "forwards" }}
          >
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Newest Listings
            </h2>
            <p className="text-gray-600 text-lg">
              Discover the latest premium cattle from top breeders
            </p>
          </div>

          <Tabs
            value={activeTab}
            onValueChange={setActiveTab}
            className="w-full"
          >
            <div
              className="flex justify-center mb-8 opacity-0 animate-fade-in"
              style={{ animationDelay: "200ms", animationFillMode: "forwards" }}
            >
              <TabsList className="bg-white border border-gray-200 shadow-sm w-full max-w-md">
                <TabsTrigger
                  value="all"
                  className="data-[state=active]:bg-green-700 data-[state=active]:text-white flex-1 transition-all duration-200"
                >
                  All
                </TabsTrigger>
                <TabsTrigger
                  value="bull"
                  className="data-[state=active]:bg-green-700 data-[state=active]:text-white flex-1 transition-all duration-200"
                >
                  Bulls
                </TabsTrigger>
                <TabsTrigger
                  value="cow"
                  className="data-[state=active]:bg-green-700 data-[state=active]:text-white flex-1 transition-all duration-200"
                >
                  Cows
                </TabsTrigger>
              </TabsList>
            </div>

            <TabsContent
              value="all"
              className="transition-opacity duration-300 ease-in-out"
            >
              {renderListingsContent(filteredListings)}
            </TabsContent>

            <TabsContent
              value="bull"
              className="transition-opacity duration-300 ease-in-out"
            >
              {renderListingsContent(filteredListings)}
            </TabsContent>

            <TabsContent
              value="cow"
              className="transition-opacity duration-300 ease-in-out"
            >
              {renderListingsContent(filteredListings)}
            </TabsContent>
          </Tabs>

          <div
            className="text-center mt-12 opacity-0 animate-fade-in"
            style={{ animationDelay: "400ms", animationFillMode: "forwards" }}
          >
            <Link to="/marketplace">
              <Button
                variant="outline"
                size="lg"
                className="border-green-700 text-green-700 hover:bg-green-700 hover:text-white hover:scale-105 transition-all duration-200"
              >
                View All Listings
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Top Breeders Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div
            className="text-center mb-8 opacity-0 animate-fade-in"
            style={{ animationDelay: "100ms", animationFillMode: "forwards" }}
          >
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Top Breeders
            </h2>
            <p className="text-gray-600 text-lg">
              Discover our featured elite cattle breeders
            </p>
          </div>

          {topBreedersLoading ? (
            <div className="flex justify-center items-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-700"></div>
            </div>
          ) : topBreedersError ? (
            <div className="text-center py-12">
              <p className="text-gray-600">
                Unable to load top breeders at the moment.
              </p>
            </div>
          ) : topBreeders.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-600">No top breeders featured yet.</p>
            </div>
          ) : (
            <div className="relative">
              <Carousel className="w-full">
                <CarouselContent className="-ml-2 md:-ml-4">
                  {topBreeders.map((profile, index) => (
                    <CarouselItem
                      key={profile.id}
                      className="pl-2 md:pl-4 basis-full sm:basis-1/2"
                    >
                      <div
                        className="opacity-0 animate-fade-in"
                        style={{
                          animationDelay: `${index * 50}ms`,
                          animationFillMode: "forwards",
                        }}
                      >
                        <BreederProfileCard profile={profile} />
                      </div>
                    </CarouselItem>
                  ))}
                </CarouselContent>

                <div className="flex justify-center items-center gap-4 mt-6">
                  <CarouselPrevious className="static translate-y-0 translate-x-0" />
                  <CarouselNext className="static translate-y-0 translate-x-0" />
                </div>
              </Carousel>
            </div>
          )}

          <div
            className="text-center mt-12 opacity-0 animate-fade-in"
            style={{ animationDelay: "400ms", animationFillMode: "forwards" }}
          >
            <Link to="/breeders">
              <Button
                variant="outline"
                size="lg"
                className="border-green-700 text-green-700 hover:bg-green-700 hover:text-white hover:scale-105 transition-all duration-200"
              >
                View All Breeders
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="py-16 relative bg-green-50 overflow-hidden">
        {/* Background gradient overlay */}
        {/* <div className="absolute inset-0 bg-gradient-to-r from-green-600/8 via-transparent to-emerald-600/8"></div> */}

        {/* Decorative gradient orbs */}
        {/* <div className="absolute top-0 left-1/4 w-96 h-96 bg-gradient-to-r from-green-400/25 to-emerald-300/25 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 right-1/4 w-80 h-80 bg-gradient-to-r from-emerald-400/25 to-green-300/25 rounded-full blur-3xl"></div> */}

        {/* Gradient background */}
        <div className="absolute inset-0 bg-gradient-to-br from-emerald-50 via-green-100 to-teal-50"></div>

        {/* Gradient overlay with better contrast */}
        <div className="absolute inset-0 bg-gradient-to-r from-green-600/15 via-emerald-500/10 to-teal-600/15"></div>

        {/* Gradient orbs for depth */}
        <div className="absolute -top-32 -left-32 w-96 h-96 bg-gradient-to-r from-green-400/30 to-emerald-400/20 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute top-1/4 right-1/3 w-72 h-72 bg-gradient-to-r from-teal-300/25 to-green-300/35 rounded-full blur-2xl"></div>
        <div className="absolute -bottom-24 -right-24 w-80 h-80 bg-gradient-to-r from-emerald-400/25 to-teal-400/30 rounded-full blur-3xl"></div>

        {/* Accent orb */}
        <div className="absolute top-3/4 left-1/5 w-48 h-48 bg-gradient-to-r from-green-200/40 to-emerald-200/30 rounded-full blur-2xl"></div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div
            className="text-center mb-12 opacity-0 animate-fade-in"
            style={{ animationDelay: "100ms", animationFillMode: "forwards" }}
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Find Breeders Near You
            </h2>
            <p className="text-gray-600">Explore breeders across the globe</p>
            {breedersLoading && (
              <p className="text-sm text-gray-500 mt-2">
                Loading breeder locations...
              </p>
            )}
            {breedersError && (
              <p className="text-sm text-red-500 mt-2">
                Error loading breeder locations
              </p>
            )}
          </div>

          {breedersLoading ? (
            <div className="flex justify-center items-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-700"></div>
            </div>
          ) : breedersError ? (
            <div className="text-center py-12">
              <p className="text-gray-600">
                Unable to load breeder locations at the moment.
              </p>
            </div>
          ) : breeders.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-600">
                No breeders with location data found.
              </p>
            </div>
          ) : (
            <div
              className="bg-white rounded-lg shadow-sm border border-green-100 opacity-0 animate-fade-in"
              style={{ animationDelay: "200ms", animationFillMode: "forwards" }}
            >
              <Map
                breeders={breeders}
                center={[-95.7129, 37.0902]}
                zoom={4}
                height="500px"
              />
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Index;
